import React from 'react';
import { Button } from '../../ui/button';
import {
  User, PoundSterling, Clock, Calendar, FileCheck, FileSignature, Edit3, Mail, Phone,
} from 'lucide-react';
import { formatDate } from '../utils';

const rentSuffix = (d) => d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th';

export default function TenantInfoCard({ property, onEditTenant, onNewTenancy, onEmailRenewal }) {
  return (
    <div className="property-info-section" data-testid="tenant-info-card">
      <div className="section-header-with-action">
        <h3 className="section-title-simple">
          <User size={20} />
          Tenant Information
        </h3>
        <Button
          onClick={onEditTenant}
          className="btn-secondary tenant-action-btn"
          data-testid="edit-tenant-button"
          title="For corrections and manual updates"
        >
          <Edit3 size={18} />
          <span>Edit Tenant Details</span>
        </Button>
        <Button
          onClick={onEmailRenewal}
          className="btn-secondary tenant-action-btn"
          data-testid="email-renewal-button"
          title={property.tenant_email ? `Email ${property.tenant_email} about renewal` : 'Email tenant about renewal'}
        >
          <Mail size={18} />
          <span>Email Renewal</span>
        </Button>
        <Button
          onClick={onNewTenancy}
          className="btn-primary tenant-action-btn"
          data-testid="new-tenancy-button"
          title="Start a new tenancy on this property"
        >
          <FileSignature size={18} />
          <span>New Tenancy</span>
        </Button>
      </div>

      <div className="property-info-grid">
        <div className="property-info-item">
          <div className="property-info-icon"><User size={20} /></div>
          <div className="property-info-content">
            <div className="property-info-label">Tenant Name</div>
            <div className="property-info-value">{property.tenant_name}</div>
          </div>
        </div>
        {property.tenant_phone && (
          <div className="property-info-item" data-testid="property-tenant-phone">
            <div className="property-info-icon"><Phone size={20} /></div>
            <div className="property-info-content">
              <div className="property-info-label">Tenant Phone</div>
              <div className="property-info-value">
                <a href={`tel:${property.tenant_phone}`} className="property-info-link">{property.tenant_phone}</a>
              </div>
            </div>
          </div>
        )}
        <div className="property-info-item">
          <div className="property-info-icon"><PoundSterling size={20} /></div>
          <div className="property-info-content">
            <div className="property-info-label">Monthly Rent</div>
            <div className="property-info-value">£{property.rent_amount}</div>
          </div>
        </div>
        <div className="property-info-item">
          <div className="property-info-icon"><Clock size={20} /></div>
          <div className="property-info-content">
            <div className="property-info-label">Rent Due Date</div>
            <div className="property-info-value">{property.rent_due_date}{rentSuffix(property.rent_due_date)} of month</div>
          </div>
        </div>
        <div className="property-info-item">
          <div className="property-info-icon"><Calendar size={20} /></div>
          <div className="property-info-content">
            <div className="property-info-label">Tenancy Start</div>
            <div className="property-info-value">{formatDate(property.tenancy_start_date)}</div>
          </div>
        </div>
        <div className="property-info-item">
          <div className="property-info-icon"><Calendar size={20} /></div>
          <div className="property-info-content">
            <div className="property-info-label">Tenancy End</div>
            <div className="property-info-value">{formatDate(property.tenancy_end_date)}</div>
          </div>
        </div>
        <div className="property-info-item">
          <div className="property-info-icon"><FileCheck size={20} /></div>
          <div className="property-info-content">
            <div className="property-info-label">Contract Type</div>
            <div className="property-info-value">{property.contract_type}</div>
          </div>
        </div>
        <div className="property-info-item" data-testid="property-tenant-email">
          <div className="property-info-icon"><Mail size={20} /></div>
          <div className="property-info-content">
            <div className="property-info-label">Tenant Email</div>
            <div className="property-info-value">
              {property.tenant_email ? (
                <a href={`mailto:${property.tenant_email}`} className="property-info-link">{property.tenant_email}</a>
              ) : (
                <span className="property-info-empty">Not set — add via Edit Tenant Details</span>
              )}
            </div>
          </div>
        </div>
        {property.deposit_amount != null && (
          <div className="property-info-item" data-testid="property-deposit">
            <div className="property-info-icon"><PoundSterling size={20} /></div>
            <div className="property-info-content">
              <div className="property-info-label">Deposit</div>
              <div className="property-info-value">£{Number(property.deposit_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
        )}
        {property.contract_value != null && (
          <div className="property-info-item" data-testid="property-contract-value">
            <div className="property-info-icon"><PoundSterling size={20} /></div>
            <div className="property-info-content">
              <div className="property-info-label">Contract Value</div>
              <div className="property-info-value">£{Number(property.contract_value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
        )}
      </div>

      {(property.previous_tenancies || []).length > 0 && (
        <details className="previous-tenancies" data-testid="previous-tenancies">
          <summary className="previous-tenancies-summary">
            <span>Previous Tenancies</span>
            <span className="previous-tenancies-count">{property.previous_tenancies.length}</span>
          </summary>
          <div className="previous-tenancies-list">
            {[...property.previous_tenancies].reverse().map((pt, idx) => (
              <div key={idx} className="previous-tenancy-row" data-testid={`previous-tenancy-${idx}`}>
                <div className="previous-tenancy-head">
                  <strong>{pt.tenant_name || 'Unknown tenant'}</strong>
                  <span className="previous-tenancy-when">
                    {formatDate(pt.tenancy_start_date)} → {formatDate(pt.tenancy_end_date)}
                  </span>
                </div>
                <div className="previous-tenancy-meta">
                  <span>£{pt.rent_amount}/mo</span>
                  <span>{pt.contract_type || 'AST'}</span>
                  {pt.deposit_amount != null && <span>Deposit £{Number(pt.deposit_amount).toLocaleString('en-GB')}</span>}
                  {pt.archived_at && <span className="previous-tenancy-archived">Archived {formatDate(pt.archived_at)}</span>}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
